<vs-dialog
  #editEmailTemplateDialog
  [config]="dialogConfig ?? { dialogContainerClass: 'max-w-[700px]' }"
  (closed)="email = null"
>
  <vs-dialog-header
    *ngIf="email"
    [config]="{
      mode: 'simple',
      hideCloseButton: false,
  
      icon: 'edit-01',
      iconTheme: 'primary',

      showDivider: true,

      title: email.type,
      subtitle: email.subject ?? ''
    }"
  ></vs-dialog-header>

  <form [formGroup]="form" class="mt-3">
    <vs-alert *ngIf="email?.type === emailType.loginLink" theme="info" class="mb-4 block">
      <div class="flex items-start gap-2">
        <vs-icon name="info-circle" size="16" class="mt-0.5"></vs-icon>
        <div>
          <p class="text-sm font-semibold">Tenant-wide template</p>
          <p class="text-sm mt-1">{{ tenantWideEmailNote }}</p>
        </div>
      </div>
    </vs-alert>

    <div class="grid grid-cols-[1fr_auto] gap-6">
      <div>
        <p class="text-sm text-gray-700 font-medium">Enabled</p>
        <p class="text-sm text-gray-600">Control whether this email is automatically sent to customers</p>
      </div>

      <input vsInput type="checkbox" appearance="toggle" class="mt-1 mr-1" formControlName="enabled" />
    </div>

    <vs-form-field theme="primary" [config]="{ label: 'Delivery Method' }" class="mt-4">
      <input vsInput id="delivery-mode-input" type="text" [value]="deliveryModeLabel" disabled />
    </vs-form-field>
    <vs-form-field theme="primary" [config]="{ label: 'Template Label' }" class="mt-4">
      <input vsInput id="template-label-input" type="text" formControlName="templateLabel" />
    </vs-form-field>

    <ng-container *ngIf="isScheduledTemplate">
      <vs-form-field theme="primary" [config]="{ label: 'Schedule Anchor' }" class="mt-4">
        <vs-select vsInput formControlName="scheduleAnchor">
          <li *ngFor="let anchor of scheduleAnchors" vs-select-option [value]="anchor.value">{{ anchor.label }}</li>
        </vs-select>
      </vs-form-field>

      <div class="mt-4">
        <label class="text-sm text-gray-700 font-medium">Schedule Offsets (days)</label>
        <p class="text-sm text-gray-600">Add offsets as negative, zero, or positive whole days.</p>
        <div class="mt-2 flex gap-2">
          <input
            vsInput
            type="text"
            [value]="offsetInput"
            (input)="offsetInput = $any($event.target).value"
            placeholder="e.g. -7, 0, +15"
          />
          <button vs-button size="sm" appearance="outline" type="button" (click)="addOffsetFromInput()">Add</button>
        </div>
        <p *ngIf="offsetError" class="text-xs text-red-600 mt-2">{{ offsetError }}</p>
        <div class="mt-2 flex flex-wrap gap-2">
          <span
            *ngFor="let offset of scheduleOffsetsDays"
            class="inline-flex items-center gap-2 rounded-full bg-gray-100 px-3 py-1 text-xs text-gray-700"
          >
            {{ offset > 0 ? '+' + offset : offset }}
            <button
              type="button"
              class="text-gray-500 hover:text-gray-900"
              aria-label="Remove offset"
              (click)="removeOffset(offset)"
            >
              &times;
            </button>
          </span>
        </div>
      </div>
    </ng-container>

    <div class="mt-4">
      <label class="text-sm text-gray-700 font-medium">Additional Recipients</label>
      <p class="text-sm text-gray-600">Enter one or more comma-separated email addresses.</p>
      <div class="mt-2 flex gap-2">
        <input
          vsInput
          type="text"
          [value]="recipientInput"
          (input)="recipientInput = $any($event.target).value"
          placeholder="ops@example.com, manager@example.com"
        />
        <button vs-button size="sm" appearance="outline" type="button" (click)="addRecipientsFromInput()">Add</button>
      </div>
      <p *ngIf="recipientError" class="text-xs text-red-600 mt-2">{{ recipientError }}</p>
      <div class="mt-2 flex flex-wrap gap-2">
        <span
          *ngFor="let recipient of additionalRecipients"
          class="inline-flex items-center gap-2 rounded-full bg-gray-100 px-3 py-1 text-xs text-gray-700"
        >
          {{ recipient }}
          <button
            type="button"
            class="text-gray-500 hover:text-gray-900"
            aria-label="Remove recipient"
            (click)="removeRecipient(recipient)"
          >
            &times;
          </button>
        </span>
      </div>
    </div>

    <vs-form-field theme="primary" [config]="{ label: 'Subject' }" class="mt-4">
      <input vsInput id="basic-text-input" type="text" formControlName="subject" />
    </vs-form-field>

    <vs-form-field theme="primary" [config]="{ label: 'Byline' }" class="mt-4">
      <input vsInput id="basic-text-input" type="text" formControlName="byline" />
    </vs-form-field>

    <label class="text-gray-700 font-medium" style="font-size: 0.875rem; line-height: 2.25rem">Content</label>
    <vs-rich-text-editor *ngIf="email" formControlName="content"></vs-rich-text-editor>

    <section *ngIf="isWarrantyProvisionedTemplate" class="mt-5 border border-gray-200 rounded-lg p-4">
      <p class="text-sm font-semibold text-gray-700">Attachments</p>
      <p class="text-xs text-gray-500 mt-1">
        Choose the WarrantySphere documents and optional uploaded files that customers will receive.
      </p>

      <div class="mt-4 flex flex-col gap-3">
        <label *ngFor="let option of systemAttachmentOptions" class="flex items-center gap-3 text-sm text-gray-700">
          <input
            vsInput
            type="checkbox"
            [checked]="hasSystemAttachment(option.value)"
            (change)="toggleSystemAttachment(option.value)"
          />
          {{ option.label }}
        </label>
      </div>

      <div *ngIf="uploadedAttachments.length" class="mt-4">
        <p class="text-sm font-medium text-gray-700 mb-2">Currently attached files</p>
        <div class="flex flex-col gap-2">
          <div
            *ngFor="let attachment of uploadedAttachments"
            class="flex items-center justify-between gap-3 rounded-lg border border-gray-200 px-3 py-2"
          >
            <div class="flex items-center gap-2 min-w-0">
              <vs-icon name="attachment-02" size="sm" class="text-gray-500 shrink-0"></vs-icon>
              <span class="text-sm text-gray-700 truncate">{{ attachment.filename }}</span>
            </div>
            <button
              type="button"
              vs-icon-button
              appearance="outline"
              size="xs"
              aria-label="Remove uploaded attachment"
              (click)="removeUploadedAttachment(attachment.storageItemId)"
            >
              <vs-icon name="trash-01" size="xs"></vs-icon>
            </button>
          </div>
        </div>
      </div>

      <div class="mt-4">
        <p class="text-sm font-medium text-gray-700 mb-2">Add uploaded documents</p>
        <vs-file-input
          formControlName="newAttachments"
          [multiple]="true"
          [moreInfo]="'Multiple files supported. Files upload when you update the template.'"
          [emptyStateText]="'Documents you select will be listed here.'"
        ></vs-file-input>
      </div>

      <p
        *ngIf="systemAttachments.length === 0 && uploadedAttachments.length === 0 && newAttachments.length === 0"
        class="text-xs text-gray-500 mt-3"
      >
        No attachments selected.
      </p>
    </section>
  </form>

  <ng-container *ngIf="tokens.length">
    <div class="mt-5 border border-gray-200 rounded-lg p-4">
      <p class="text-sm font-semibold text-gray-700 mb-1">Available Tokens</p>
      <p class="text-xs text-gray-500 mb-3">
        Click a token to copy it, then paste it into the subject or content fields above.
      </p>

      <div class="flex flex-col gap-2">
        <div *ngFor="let t of tokens" class="flex items-start justify-between gap-3">
          <div class="flex-1 min-w-0">
            <p class="text-xs text-gray-500">{{ t.description }}</p>
            <p class="text-xs text-gray-400 italic">e.g. {{ t.example }}</p>
          </div>
          <button
            vs-button
            size="sm"
            appearance="outline"
            class="font-mono text-xs shrink-0"
            [cdkCopyToClipboard]="t.token"
            (cdkCopyToClipboardCopied)="copyToken(t.token)"
          >
            <vs-icon *ngIf="copiedToken === t.token" name="check" size="xs"></vs-icon>
            <vs-icon *ngIf="copiedToken !== t.token" name="copy-01" size="xs"></vs-icon>
            {{ t.token }}
          </button>
        </div>
      </div>
    </div>
  </ng-container>

  <div class="flex footer mt-5 gap-2 justify-end">
    <button vs-button size="md" appearance="outline" [disabled]="saving" (click)="cancel()">Cancel</button>
    <button vs-button size="md" theme="primary" [disabled]="saving || !form.dirty" (click)="submit()">Update</button>
  </div>
</vs-dialog>
